/* @flow */
import * as React from 'react';

import { assertions } from 'lib/assertions';
import AuthActions from 'auth/AuthActions';
import AuthenticationView from 'auth/components/AuthenticationView';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Cookies from 'lib/Cookies';
import Copy from 'components/Copy';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import LinkDeviceVerificationCodeBox from 'user/components/LinkDeviceVerificationCodeBox';
import TitleHeader from 'components/TitleHeader';
import ToastActions from 'actions/ToastActions';
import ErrorReporter from 'app/components/ErrorReporter';

import {
  RESET_PASSWORD_CODE_COOKIE,
  RESET_PASSWORD_SMS_ADDRESS_COOKIE,
} from 'constants/AuthConstants';

type Props = {
  location: Object,
  router: Object,
};

type State = {
  // uid is the user inputted email or phone number
  uid?: string,
  // address is the email or phone number we get in the
  // server response from requesting a password reset
  address: string,
  requestSubmitted: boolean,
};

class ForgotPasswordPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      uid: props.location.query.email || '',
      address: '',
      requestSubmitted: false,
    };
  }

  handleUidChange = (uid?: string) => {
    this.setState({ uid });
  };

  saveCode = (code: string) => {
    Cookies.set(RESET_PASSWORD_CODE_COOKIE, code);
    Cookies.set(RESET_PASSWORD_SMS_ADDRESS_COOKIE, this.state.uid);
    this.props.router.push('/reset_password/');
  };

  handleSubmit = () => {
    const { uid } = this.state;
    if (!uid) {
      console.error('ForgotPasswordPage - tried to submit without a uid');
      return;
    }

    const onSuccess = response => {
      this.setState({
        address: response.address,
        requestSubmitted: true,
      });
    };

    const onError = error => ToastActions.showAPIError(error);

    AuthActions.requestPasswordReset(uid)
      .then(onSuccess)
      .catch(onError);
  };

  renderSent() {
    // TODO: should render into an `AuthenticationView`, but requires `LinkDeviceVerificationCodeBox`
    // scaffolding refactoring
    const { address, uid } = this.state;
    if (uid && address && address.startsWith('sms://')) {
      return (
        <LinkDeviceVerificationCodeBox
          submittedAddress={uid}
          address={address}
          onSubmit={this.saveCode}
          onResend={this.handleSubmit}
          successMessage="Correct!"
          deviceType="phoneNumber"
        />
      );
    } else if (uid && address && address.startsWith('email://')) {
      return (
        <div>
          <TitleHeader image={require('assets/images/email-sent.svg')} title="Email sent" />
          <Copy align="center">
            <p>
              Please check your email for instructions on how to reset your password! If you do not
              receive anything, please check your spam folder for an email from remind.com.
            </p>
            <p>
              <Link href={LinkConstants.FORGOT_PASSWORD} target="_blank">
                Click here
              </Link>{' '}
              if you need further assistance.
            </p>
          </Copy>
        </div>
      );
    }

    return <ErrorReporter error="Reset password address not an email or sms" />;
  }

  renderForm() {
    return (
      <div>
        <FormElement
          label="Email address or phone number"
          onValidChange={e => this.handleUidChange(e.target.value)}
          onInvalidChange={() => this.handleUidChange(undefined)}
          validate={e => {
            assertions.emailOrPlausibleNorthAmericanPhoneNumber(
              e.target.value,
              'Invalid phone number or email address. Please try again.'
            );
          }}
          element={Input}
          elementProps={{
            autocomplete: 'username',
            defaultValue: this.props.location.query.email || '',
            name: 'uid',
            placeholder: 'Email address or phone number',
            size: 'large',
            required: true,
          }}
        />
        <Buttons axis="y">
          <Button
            type="submit"
            className="ForgotPassword-submit"
            level="primary"
            full
            eventElementName="ForgotPassword-submit"
            onClick={this.handleSubmit}
            disabled={!this.state.uid}>
            Send reset instructions
          </Button>
          <Button
            href={LinkConstants.FORGOT_PASSWORD}
            target="_blank"
            color="brand"
            level="text"
            data-test="help-button">
            Help
          </Button>
        </Buttons>
      </div>
    );
  }

  render() {
    return (
      <AuthenticationView {...this.props} title="Forgot password?" className="ForgotPasswordPage">
        {this.state.requestSubmitted ? this.renderSent() : this.renderForm()}
      </AuthenticationView>
    );
  }
}

export default ForgotPasswordPage;
